import { Box, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import treffas from "src/theme/treffas";

const { colors, typography, radius, spacing } = treffas;

const DetailRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: spacing[4],
      py: spacing[3],
      borderBottom: `1px solid ${colors.border.default}`,
      "&:last-of-type": {
        borderBottom: "none",
        pb: 0,
      },
      "&:first-of-type": {
        pt: 0,
      },
    }}
  >
    <Typography
      sx={{
        fontFamily: typography.fontFamily.body,
        fontSize: typography.fontSize.md,
        fontWeight: typography.fontWeight.regular,
        color: colors.text.secondary,
        lineHeight: typography.lineHeight.normal,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: typography.fontFamily.body,
        fontSize: typography.fontSize.md,
        fontWeight: typography.fontWeight.medium,
        color: colors.text.strong,
        lineHeight: typography.lineHeight.normal,
        textAlign: "right",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const DetailsCard = ({ title, children }) => (
  <Box
    sx={{
      backgroundColor: colors.background.default,
      border: `1px solid ${colors.border.default}`,
      borderRadius: radius.xl,
      p: { xs: spacing[5], md: spacing[6] },
      height: "100%",
    }}
  >
    <Typography
      sx={{
        fontFamily: typography.fontFamily.headingMedium,
        fontSize: typography.fontSize.xl,
        lineHeight: typography.lineHeight.tight,
        color: colors.text.strong,
        mb: spacing[4],
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const SessionDetails = ({ sessionDetails }) => {
  const { t } = useTranslation();
  const remaining =
    parseInt(sessionDetails?.sessions_totalcount) -
    parseInt(sessionDetails?.session_records_count);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: spacing[4],
      }}
    >
      <DetailsCard title={t("next.booking.label.client_details")}>
        <DetailRow
          label={t("next.booking.label.name")}
          value={`${sessionDetails?.client?.firstname ?? ""} ${
            sessionDetails?.client?.lastname ?? ""
          }`.trim()}
        />
        <DetailRow
          label={t("next.booking.label.email")}
          value={sessionDetails?.client?.email}
        />
      </DetailsCard>

      <DetailsCard title={t("next.booking.label.session_details")}>
        <DetailRow
          label={t("next.booking.label.total_session")}
          value={sessionDetails?.sessions_totalcount}
        />
        <DetailRow
          label={t("next.booking.label.total_session_used")}
          value={sessionDetails?.session_records_count}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: spacing[4],
            pt: spacing[3],
          }}
        >
          <Typography
            sx={{
              fontFamily: typography.fontFamily.body,
              fontSize: typography.fontSize.md,
              color: colors.text.secondary,
            }}
          >
            {t("next.booking.label.total_session_remaining")}
          </Typography>
          <Box
            sx={{
              minWidth: "32px",
              px: spacing[3],
              py: "2px",
              borderRadius: radius.pill,
              backgroundColor: colors.brand.primarySoft,
              color: colors.brand.primary,
              fontFamily: typography.fontFamily.body,
              fontSize: typography.fontSize.md,
              fontWeight: typography.fontWeight.semibold,
              textAlign: "center",
            }}
          >
            {remaining}
          </Box>
        </Box>
      </DetailsCard>
    </Box>
  );
};

export default SessionDetails;
